/* eslint-disable @typescript-eslint/no-explicit-any */
import { coerceCssPixelValue, coerceNumberProperty, coerceStringArray } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { isAny, isNotNullOrUndefined, roundTo } from '@trilean/n7';
import { ResizeObserver } from '@juggle/resize-observer';

export type SparklineType = 'line' | 'bar' | 'column';

@Component({
  selector: 'tr3-sparkline',
  templateUrl: './sparkline.component.html',
  styleUrls: ['./sparkline.component.scss'],
  host: {
    '[style.width]': 'width',
    '[style.height]': 'getLineHeight()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparklineComponent implements OnChanges, OnDestroy {
  @Input() type: SparklineType = 'line';
  @Input() values?: (number | null)[];
  @Input() min: number | null = null;
  @Input() max?: number | null = null;
  @Input() palette?: string[];
  @Input() width?: string;
  @Input() markers: number | false = false;

  private static readonly DefaultMarkerSize = 4;

  contentWidth = 0;
  contentHeight = 0;

  private _resizeObserver: ResizeObserver;

  constructor(private _elementRef: ElementRef, private _cd: ChangeDetectorRef) {
    this._resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => this.handleResize(entries));
    this._resizeObserver.observe(_elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this._resizeObserver.disconnect();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (_changes['min']) {
      this.min = coerceNumberProperty(_changes['min'].currentValue, null);
    }

    if (_changes['max']) {
      this.max = coerceNumberProperty(_changes['max'].currentValue, null);
    }

    if (_changes['palette']) {
      this.palette = coerceStringArray(_changes['palette'].currentValue);
    }

    if (_changes['markers']) {
      const currentValue = _changes['markers'].currentValue;
      this.markers = isAny(currentValue, false, null, undefined)
        ? false
        : coerceNumberProperty(currentValue, SparklineComponent.DefaultMarkerSize);
    }

    if (_changes['width']) {
      this.width = coerceCssPixelValue(_changes['width'].currentValue) || undefined;
    }

    if (_changes['values']) {
      const currentValue = _changes['values'].currentValue;
      this.values = (Array.isArray(currentValue) ? currentValue : coerceStringArray(currentValue)).map((e) =>
        coerceNumberProperty(e, null),
      );
    }

    this._cd.markForCheck();
  }

  getColor(i = 0): string | undefined {
    if (!this.palette?.length) return undefined;
    const p = this.palette;
    return p[i % p.length];
  }

  getLineHeight(): string {
    return window.getComputedStyle(this._elementRef.nativeElement)['line-height'];
  }

  getBarWidth(value: number): number {
    if (!this.values?.length) return 0;
    const values = <number[]>[...this.values];
    const minX = 0;
    const maxX = this.max ?? values.reduce((acc, v) => acc + v, 0);
    const rangeX = Math.max(maxX - minX, 1e-5);
    const scaleX = this.contentWidth / rangeX;
    return (value - minX) * scaleX;
  }

  getColumnHeight(value: number): number {
    if (!this.values?.length) return 0;
    const values = <number[]>[...this.values];
    const minY = this.min ?? values.reduce((acc, v) => Math.min(acc ?? v, v));
    const maxY = this.max ?? values.reduce((acc, v) => Math.max(acc ?? v, v));
    const rangeY = Math.max(maxY - minY, 1e-5);
    const maxHeight = this.contentHeight - 1;
    const scaleY = maxHeight / rangeY;
    return (value - minY) * scaleY + 1;
  }

  private getLinePoints(): [x: number, y: number | null][] {
    if (!this.values?.length) return [];
    const values = <number[]>[...this.values];
    if (values.length == 1) values.push(values[0]);
    const minY = this.min ?? values.reduce((acc, v) => Math.min(acc ?? v, v));
    const maxY = this.max ?? values.reduce((acc, v) => Math.max(acc ?? v, v));
    const rangeX = Math.max(this.values.length - 1, 1e-5);
    const rangeY = Math.max(maxY - minY, 1e-5);
    const scaleX = this.contentWidth / rangeX;
    const scaleY = this.contentHeight / rangeY;
    return this.values.map((v, i) => [
      roundTo(i * scaleX, 3),
      isNotNullOrUndefined(v) ? roundTo(this.contentHeight - (v - minY) * scaleY, 3) : null,
    ]);
  }

  getLinePath(): string {
    const points = this.getLinePoints();
    return points
      .map(([x, y], i, a) => [x, y, a[i - 1]?.[1] == null])
      .filter(([, y]) => y != null)
      .map(([x, y, s]) => (s ? `M${x} ${y}L${x} ${y}` : `${x} ${y}`))
      .join(' ');
  }

  getMarkersPath(): string {
    if (!this.markers) return '';
    const points = this.getLinePoints();
    const r = this.markers * 0.5;
    if (r < 2) {
      return (
        points
          .filter(([, y]) => y != null)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .map(([x, y]) => `M${x - r} ${y! - r}h${r * 2}v${r * 2}h${-r * 2}z`)
          .join(' ')
      );
    } else {
      return points
        .filter(([, y]) => y != null)
        .map(([x, y]) => `M${x - r} ${y} a${r} ${r} 0 1 0 ${r * 2} 0 a${r} ${r} 0 1 0 ${-r * 2} 0`)
        .join(' ');
    }
  }

  columnTrackByIndex(i: number): number {
    return i;
  }

  private handleResize(entries: ResizeObserverEntry[]): void {
    const r = entries[0]?.contentRect;
    this.contentWidth = r?.width ?? 0;
    this.contentHeight = r?.height ?? 0;
    this._cd.markForCheck();
  }
}
